import React, {useState} from 'react';
import {Box, Button, Card, CardContent, Modal, Paper, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import Grid from "@mui/material/Grid2";
import CartItemParams from "../../../checkout/components/CartItemParams";
import IconButton from "@mui/material/IconButton";
import {ClearOutlined} from "@mui/icons-material";
import OrderDesigns from "./OrderDesigns";
import FunctionHandler from "../../../utils/functionHandler";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StatusIcon from "./StatusIcon";
import {getStatusNumber, statuses} from "../../../adminPages/Components/AdminOrderCard";
import UserInfo from "../../../checkout/components/billingData/UserInfo";

function OrderCard({order}) {
    const {t} = useTranslation()
    const [open, setOpen] = useState(false)
    const {items, shipping, nr, status, discount, invoiceData} = order;

    const getTotalPrice = () => {
        let total = 0;
        Object.keys(items).forEach(itemKey => {
            const item = items[itemKey];
            total += (item.product.price * item.nr * (1 - item.discount))
        });

        total = total * (1 - (discount || 0));
        total += shipping.price;
        return total;
    }

    const getImages = (item, product) => {
        let images = product.images;

        if (product.necessaryDesigns && Number(product.necessaryDesigns)) {
            images = order.selectedDesigns?.filter(design => design.value?.size === item.params?.size?.label).map(design => ({
                src: FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent('thumbnail/')}${design.value.src.replace(/.svg$/, '.png')}`),
                nr: design.value.nr
            }));
        }

        if (item.params?.file?.path) {
            images = [{
                src: FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent('thumbnail/')}${item.params?.file?.path.replace(/.svg$/, '.png')}`),
                nr: 1
            }];
        }

        return <OrderDesigns images={images}/>;
    }

    const totalPrice = order?.totalPrice || getTotalPrice();

    return (
        <Card sx={{maxWidth: 500}}>
            <Box sx={{pt: 2}}>
                <Stepper alternativeLabel activeStep={getStatusNumber(status)} sx={{padding: 0}}>
                    {statuses.map((label) => (
                        <Step key={label}>
                            <StepLabel slots={{stepIcon: StatusIcon}} slotProps={{labelName: label}}/>
                        </Step>
                    ))}
                </Stepper>
            </Box>
            <CardContent>
                <Typography variant="h5" component="div">
                    <Trans i18nKey="orderCard.title">Order Details</Trans>
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="body1">
                            <Trans i18nKey="orderCard.orderNr">Order Number:</Trans>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {nr}
                        </Typography>
                    </Grid>

                    {shipping.type === 'normal' &&
                        <Grid size={12}>
                            <Typography variant="body1">
                                <Trans i18nKey="orderCard.shippingAddress">Shipping Address:</Trans>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {shipping.address.firstName} {shipping.address.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {shipping.address.addressLine1}, {shipping.address.addressLine2 ? (shipping.address.addressLine2 + ', ') : ''}
                                {shipping.address.city}, {shipping.address.county}, {shipping.address.zipCode}, {shipping.address.country}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Trans i18nKey="orderCard.phone">Phone:</Trans> {shipping.address.telNr}
                            </Typography>
                        </Grid>}
                    {shipping.type === 'easyBox' &&
                        <Grid size={12}>
                            <Typography variant="body1">
                                <Trans i18nKey="orderCard.easyBoxAddress">Easybox Address:</Trans>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {shipping.easyBox.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {shipping.easyBox.address}, {shipping.easyBox.city}, {shipping.easyBox.county}, {shipping.easyBox.postalCode}
                            </Typography>
                        </Grid>}
                    {shipping.type === 'personal' && <Typography>
                        <Trans i18nKey={'shippingForm.personal'}/>
                    </Typography>}

                    {invoiceData?.userInfo ?
                        <Grid size={12}>
                            <Typography variant="body1">
                                <Trans i18nKey="orderCard.invoice">Invoice details:</Trans>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {invoiceData.userInfo.firstName} {invoiceData.userInfo.lastName}
                            </Typography>
                            {invoiceData.type === UserInfo.types.JURIDICAL_PERSON && <Typography variant="body2" color="text.secondary">
                                {invoiceData.userInfo.firmName}
                            </Typography>}
                            {invoiceData.type === UserInfo.types.JURIDICAL_PERSON && <Typography variant="body2" color="text.secondary">
                                {invoiceData.userInfo.vatNr}
                            </Typography>}
                            <Typography variant="body2" color="text.secondary">
                                {invoiceData.userInfo.addressLine1}, {invoiceData.userInfo.addressLine2 ? (invoiceData.userInfo.addressLine2 + ', ') : ''}
                                {invoiceData.userInfo.city}, {invoiceData.userInfo.county}, {invoiceData.userInfo.zipCode}, {invoiceData.userInfo.country}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Trans i18nKey="orderCard.phone">Phone:</Trans> {invoiceData.userInfo.telNr}
                            </Typography>
                        </Grid> : ''}

                    <Grid size={12}>
                        <Typography variant="body1">
                            <Trans i18nKey="orderCard.payment">Payment Type:</Trans>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t(`paymentForm.${order.paymentType}`)}
                        </Typography>
                    </Grid>

                    <Grid size={12}>
                        <Typography variant="body1">
                            <Trans i18nKey="orderCard.totalPrice">Total Price:</Trans>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {totalPrice.toFixed(2)} Lei
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{mt: 2, display: 'flex', justifyContent: 'flex-end'}}>
                    <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                        <Trans i18nKey="orderCard.viewProducts">View Products</Trans>
                    </Button>
                </Box>
            </CardContent>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
            >
                <Paper sx={{width: {xs: '100%', sm: 500}, maxHeight: '100%', overflowY: 'auto', overflowX: 'hidden'}}>
                    <IconButton onClick={() => setOpen(false)}
                                sx={{position: 'absolute', top: -5, right: -5, zIndex: 100}}>
                        <ClearOutlined/>
                    </IconButton>
                    <CardContent>
                        <Typography variant="h6">
                            <Trans i18nKey={'header.Products'}/>:
                        </Typography>
                    </CardContent>
                    {order.totalDiscountAmount ? <CardContent key={"discount"} sx={{pt: 0}}>
                        <Grid container>
                            <Grid flexGrow={1}>
                                <Typography variant="body1">
                                    <Trans i18nKey={`cart.discount`}/>
                                </Typography>
                            </Grid>
                            <Grid>
                                <Typography variant="body1">
                                    {order.totalDiscountAmount.toFixed(2)} Lei
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent> : ''}
                    <CardContent key={"shipping"} sx={{pt: 0}}>
                        <Grid container>
                            <Grid flexGrow={1}>
                                <Typography variant="body1">
                                    <Trans i18nKey={`cart.shipping`}/>
                                </Typography>
                                <Typography variant="caption">
                                    <Trans i18nKey={`shippingForm.${shipping.type}`}/>
                                </Typography>
                            </Grid>
                            <Grid>
                                <Typography variant="body1">
                                    {shipping.price} Lei
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                    {Object.keys(items).map(key => {
                        const item = items[key];
                        const {product} = item;
                        return <CardContent key={key} sx={{pt: 0}}>
                            <Grid container>
                                <Grid flexGrow={1}>
                                    <Typography variant="body1">
                                        <Trans i18nKey={product.name}/> (x{item.nr})
                                    </Typography>
                                    <Typography variant="caption">
                                        ({(item.discountedPrice || 0).toFixed(2)} Lei)
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <Typography variant="body1">
                                        {((item.discountedTotalPrice || 0)).toFixed(2)} Lei
                                    </Typography>
                                </Grid>
                            </Grid>
                            {product.params ? <Box sx={{pt: 1}}>
                                <CartItemParams params={product.params} values={item.params} itemKey={key}
                                                productId={product.id} disableEdit/>
                            </Box> : ''}
                            {getImages(item, product)}
                        </CardContent>
                    })}
                </Paper>
            </Modal>
        </Card>
    );
}

export default OrderCard;
